import { css, html } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { styleMap } from 'lit/directives/style-map.js';
import { useEffect } from 'haunted';
import type { DataType } from 'csstype'
import * as animations from '@shoelace-style/animations'
import { easings } from '@shoelace-style/animations'
import { LitHauntedElement } from '../mixins/lit-haunted-element'

const DEFAULT_ENTRANCE: Keyframe[] = [
  { offset: 0, opacity: '0', transform: 'translate3d(calc(-1 * clamp(5px, 5%, 15px)), 0, 0)' },
  { offset: 1, opacity: '1', transform: 'translate3d(0, 0, 0)' }
]; 
const DEFAULT_EXIT: Keyframe[] = [
  { offset: 0, opacity: '1' },
  { offset: 1, opacity: '0', transform: 'translate3d(calc(1 * clamp(5px, 5%, 15px)), 0, 0)' }
];

//#region <ep-multi-view>

@customElement('ep-multi-view')
export class EPMultiView extends LitHauntedElement {
  #instanceNumber: number = Math.floor(Math.random() * 100) + 1;

  @property({ attribute: 'value', reflect: true })
  value?: string;

  @query('slot')
  slotElement?: HTMLSlotElement;

  get views(): EPView[] {
    return (this.slotElement?.assignedElements() ?? []).filter(e => e instanceof EPView) as EPView[];
  }

  @property({ type: Boolean, attribute: 'debug', reflect: true })
  debug: boolean = false;

  private _handleValueChange() {
    const views = this.views;
    // Get the views that will and won't disappear
    const willAppear = views.find(v => v.value === this.value);
    const willDisappear = views.filter(v => v !== willAppear);

    // Set the `visible` value and rely on the <ep-view> itself to animate itself
    if (willAppear !== undefined) {
      willAppear.visible = true;
    }
    for(let view of willDisappear) {
      view.visible = false;
    }

    console.debug(`[${this.tagName}#${this.#instanceNumber}] HandleValueChange`, 'hasUpdated?', this.hasUpdated, 'willAppear?', willAppear, 'willDisappear?', willDisappear);
  }

  render() {
    useEffect(() => this._handleValueChange(), [this.value])

    return html`
      ${
        this.debug
        ? html`<span id="debug">ep-multi-view value: ${this.value}</span>`
        : ''
      }
      <div class="wrap" style=${styleMap({
        outline: this.debug ? '1px dashed blue' : 'none'
      })}>
        <slot></slot>
      </div>
    `
  }

  static styles = css`
    :host {
      display: block;
      /* outline: 1px dashed blue; */
    }

    #debug {
      position: fixed;
    }

    /*
    Simple method for reliably stacking children in CSS without the "gotchas" of 'position'
    See: https://css-tricks.com/how-to-stack-elements-in-css/#aa-method-2-using-css-grid

    Explanation: Make a CSS grid and put every child in the same position.
    */
    .wrap {
      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: 1fr;
      height: 100%;
    }
  `
}

//#endregion
//#region <ep-view>

@customElement('ep-view')
export class EPView extends LitHauntedElement {
  #instanceNumber: number = Math.floor(Math.random() * 100) + 1;

  /**
   * This view will be visible if this property is equivalent to the value in EPMultiView.value
   */
  @property({ attribute: 'value', reflect: true })
  value?: string;

  @property({ type: Boolean, attribute: 'visible', reflect: true })
  visible: boolean = false;

  @state()
  shouldBeVisible: boolean = false;
  @state()
  isAnimating: boolean = false;

  //#region <ep-view> - Customize Animations

  @property({ attribute: 'animation-entrance' })
  entranceAnimationName?: string;

  @property({ attribute: 'animation-exit' })
  exitAnimationName?: string;

  @property({ attribute: false })
  entranceKeyframes: Keyframe[] = [...((animations as any)[this.entranceAnimationName ?? ''] ?? DEFAULT_ENTRANCE)];

  @property({ attribute: false })
  exitKeyframes: Keyframe[] = [...((animations as any)[this.exitAnimationName ?? ''] ?? DEFAULT_EXIT)];

  @property({ attribute: 'easing-function' })
  easingFunction: DataType.EasingFunction = easings.easeOutSine ?? 'ease-out';

  @property({ type: Number, attribute: 'animation-duration' })
  animationDuration: EffectTiming['duration'] = 125;

  //#endregion

  private _handleVisibleChange() {
    console.debug(`[${this.tagName}#${this.#instanceNumber}] HandleVisibleChange`, 'hasUpdated?', this.hasUpdated, 'visible?', this.visible);
    // On the first render, no special effects
    if (!this.hasUpdated) {
      this.shouldBeVisible = this.visible;
      return;
    }

    this.isAnimating = true;
    // Perform the animation
    const animation = this.animate(this.visible ? this.entranceKeyframes : this.exitKeyframes, {
      duration: this.animationDuration,
      iterations: 1,
      easing: this.easingFunction,
    });
    // Solidify the end-result of the animation after it has COMPLETED
    // This promise will NOT resolve if the animation is cancelled
    animation.finished.then(() => {
      this.shouldBeVisible = this.visible;
      this.isAnimating = false;
      //this.setAttribute('data-should-visible', `${this.visible}`)
      //this.style.opacity = this.visible ? '1.0' : '0.0';
    })
    animation.addEventListener('cancel', () => this.isAnimating = false);
  }

  render() {
    useEffect(() => this._handleVisibleChange(), [this.visible]);
    useEffect(() => {
      this.setAttribute('data-should-visible', `${this.shouldBeVisible}`)
    }, [this.shouldBeVisible]);
    useEffect(() => {
      this.setAttribute('data-is-animating', `${this.isAnimating}`)
    }, [this.isAnimating]);
    useEffect(() => {
      this.entranceKeyframes = [...((animations as any)[this.entranceAnimationName ?? ''] ?? DEFAULT_ENTRANCE)];
    }, [this.entranceAnimationName]);
    useEffect(() => {
      this.exitKeyframes = [...((animations as any)[this.exitAnimationName ?? ''] ?? DEFAULT_EXIT)];
    }, [this.exitAnimationName]);

    return html`
      <div class="view-wrap">
        <slot></slot>
      </div>
    `
  }

  static styles = css`
    :host {
      display: block;
      /*
      Put every <ep-view> within the same place of a CSS grid.
      This will cause them to "stack" on top of each other.
      See: https://developer.mozilla.org/en-US/docs/Web/CSS/grid-area
      */
      grid-area: 1 / 1 / 1 / 1;
      height: fit-content;
    }

    /*
    This whole mess with the [data-*] tags is so each <ep-view> can be a different size, and so that only the active and/or currently animating views take up DOM space
    */
    :host([data-should-visible=true]) {
      opacity: 1.0;
      pointer-events: auto;
      display: block;
    }
    :host([data-should-visible=false]) {
      opacity: 0.0;
      pointer-events: none;
      display: none;
    }
    :host([data-is-animating=true]) {
      display: block;
    }

    .view-wrap {
      height: 100%;
      /*
      Prevent the margin of children (such as <p> and <h1> elements) from adding space "outside" the parent element
      See: https://stackoverflow.com/a/13573739/4852536
      */
      overflow: hidden;
    }
  `
}
//#endregion

declare global {
  interface HTMLElementTagNameMap {
    'ep-multi-view': EPMultiView,
    'ep-view': EPView,
  }
}
